<script>
  import { push, link } from 'svelte-spa-router';
  import { FileEdit, Save, Send, Trash2, AlertTriangle } from '../lib/icons/index.js';
  import Layout from '../components/Layout.svelte';
  import { t } from '../i18n/index.js';
  import { activeCompanyId } from '../stores/company.js';
  import { companyInvoicesApi, customersApi } from '../stores/api.js';
  import { computeInvoiceTotals, submittedLineItems } from '../lib/totals.js';
  import { draftSavePatch, withStoredCounterpart, customerOptionLabel } from '../lib/drafts.js';
  import LineItemsEditor, { emptyLineItem, lineItemFromStored }
    from '../components/LineItemsEditor.svelte';
  import InvoiceTotalsPreview from '../components/InvoiceTotalsPreview.svelte';
  import PaymentTermsEditor from '../components/PaymentTermsEditor.svelte';
  import CorrelatiEditor from '../components/CorrelatiEditor.svelte';
  import { emptyCorrelato, correlatiFromStored, correlatiPayload, filledCorrelati, incompleteCorrelati } from '../lib/correlati.js';
  import { buildPagamento, paymentTermsFromStored } from '../lib/payment.js';
  import { isSedeEmpty, isSedeIncomplete } from '../lib/customer-sede.js';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import PromoteDialog from '../components/PromoteDialog.svelte';

  /**
   * The draft editor: unlike InvoiceCreate.svelte, nothing here is required and
   * saving is available at any time — a draft has no number and cannot reach
   * SdI, so there is nothing for an incomplete field to break. The only way out
   * of the draft is the promote dialog, which is where completeness is finally
   * demanded.
   */
  export let params = {};

  let loading = true;
  let notFound = false;
  // The loaded row, kept only for the autofattura case: that document is shown
  // as it is stored rather than edited, so the card reads from the row and not
  // from the form state below.
  let draftRow = null;
  let deleting = false;
  let customers = [];
  let customerId = '';
  // What the draft was loaded with, so a save can tell an unchanged selection
  // from a deliberate one. See draftSavePatch(): naming `customerId` on a
  // PATCH re-addresses the document, so an ordinary save must not name it.
  let loadedCustomerId = '';
  let causale = '';
  let lineItems = [emptyLineItem()];
  // The references to an order, contract or convention (and the CIG a public
  // administration requires). Read from the stored draft, so one prepared
  // through the API or the connector is visible here, and written back whole on
  // every save — see lib/correlati.js, which also keeps the fields this screen
  // does not show.
  let correlati = [emptyCorrelato()];
  let correlatiOpen = false;
  // Payment terms, in the same editor and the same shape the creation form
  // uses — a draft is promoted into an invoice, so terms typed here have to
  // produce the very object `POST /invoices` would have produced.
  //
  // With one difference the operator is told about: `anchorDate` is null,
  // because a draft has no date until it is issued. The terms are therefore
  // stated in days and the scadenza is materialised at promotion, when there
  // is finally a date to count from. See lib/payment.js.
  let paymentTerms = paymentTermsFromStored(null);
  let paymentMethod = null;
  // Whether the preset picker has already been answered on this draft. Held
  // here rather than in the editor for the same reason `paymentMethod` is —
  // see PaymentTermsEditor.svelte.
  let paymentPresetChosen = false;
  // Whether the draft arrived with payment terms of its own. It decides
  // whether the company's default payment method may be applied on top: a
  // draft prepared through the API already states what it wants.
  let hadStoredPagamento = false;
  let saving = false;
  let saved = false;
  let error = null;
  let promoting = false;

  $: companyId = $activeCompanyId;
  $: draftId = params.id;
  // Passed to the label helper rather than read inside it, so lib/drafts.js
  // stays free of Svelte and of the catalog and can be unit-tested directly.
  $: autofattura = draftRow?.metadata?.autofattura || null;
  $: counterpartLabels = {
    supplier: $t('drafts.counterpartSupplier'),
    inactive: $t('drafts.counterpartInactive'),
    missing: $t('drafts.counterpartMissing'),
    unknown: $t('drafts.counterpartUnknown'),
  };
  $: if (companyId && draftId) load();
  // The anagrafica the draft is addressed to, when there is one to read: a
  // synthesized option is only the document's own snapshot of a counterpart
  // that is gone, and has no address on file to judge.
  $: selectedCustomer = customers.find((c) => c.id === customerId && !c.synthesized) || null;
  $: selectedCustomerName = customerOptionLabel(selectedCustomer, {});
  // Shared with the parent's payload and with InvoiceTotalsPreview, so the
  // figures on screen and the ones eventually submitted cannot come apart.
  // The draft's own esigibilità, which this editor has no field for: without it
  // a split-payment draft would be summed — and its payment amount computed — as
  // if the VAT were the issuer's to collect.
  $: totals = computeInvoiceTotals(lineItems, { esigibilitaIVA: draftRow?.metadata?.esigibilitaIVA });

  async function load() {
    loading = true;
    error = null;
    notFound = false;
    try {
      const draft = await companyInvoicesApi.getDraft(companyId, draftId);
      draftRow = draft;
      // A self-invoice is shown, not edited: the parties are the reverse of an
      // ordinary invoice's and a PATCH would rebuild them the ordinary way, so
      // the API refuses one. Nothing below is needed for it.
      if (draft.metadata?.autofattura) return;
      // Only the clienti a draft could ever be issued to — the same
      // restriction InvoiceCreate.svelte applies to its own autocomplete.
      const listed = await customersApi.list(companyId, { kind: 'cliente' });
      // That list is the right set to *choose* from and the wrong one to
      // *display* from: the draft may name a fornitore, a cliente since
      // deactivated, or a record past the 200-row cap, and none of those match
      // an option — so the select rendered empty and the editor showed a draft
      // with no counterpart. `GET /customers/:id` returns the whole entity with
      // neither filter nor cap, so one call covers all three.
      let fetched = null;
      if (draft.customerId && !listed.some((c) => c.id === draft.customerId)) {
        try {
          fetched = await customersApi.get(companyId, draft.customerId);
        } catch {
          // Any failure, not only a 404: a 500 or a dropped connection must not
          // replace the editor with an error banner over a draft that is
          // perfectly editable. The metadata snapshot names the counterpart
          // well enough to work on the document.
          fetched = null;
        }
      }
      customers = withStoredCounterpart(listed, draft, fetched);
      customerId = draft.customerId || '';
      loadedCustomerId = customerId;
      causale = draft.metadata?.datiGenerali?.causale || '';
      // The editor writes `aliquotaIva`; the API stores the FatturaPA spelling
      // `aliquotaIVA`. lineItemFromStored() reconciles the two — reading the
      // stored field directly would show a 10%-taxed row as 22%.
      correlati = correlatiFromStored(draft.metadata?.datiGenerali);
      correlatiOpen = filledCorrelati(correlati).length > 0;
      const stored = draft.metadata?.lineItems || [];
      lineItems = stored.length ? stored.map(lineItemFromStored) : [emptyLineItem()];
      const storedPagamento = draft.metadata?.pagamento || null;
      hadStoredPagamento = Boolean(storedPagamento);
      paymentTerms = paymentTermsFromStored(storedPagamento);
      paymentMethod = null;
      paymentPresetChosen = false;
    } catch (err) {
      // A draft that no longer exists in `draft` status — deleted, or already
      // promoted from another tab — answers 404 rather than a body to read.
      if (err.status === 404) notFound = true;
      else error = err;
    } finally {
      loading = false;
    }
  }

  async function save() {
    saved = false;
    error = null;
    // A row with only a date or a CUP cannot be stored and would be dropped
    // silently on the way out; say so instead, before anything is sent.
    const incomplete = incompleteCorrelati(correlati);
    if (incomplete.length > 0) {
      error = $t('invoiceCreate.errors.correlatoIdRequired', { n: incomplete.join(', ') });
      correlatiOpen = true;
      return;
    }
    saving = true;
    try {
      await companyInvoicesApi.updateDraft(companyId, draftId, draftSavePatch({
        causale,
        // All five reference types, `null` for one with none, so a reference
        // deleted on screen is deleted on the draft.
        ...correlatiPayload(correlati),
        // Only the rows actually filled in — an untouched blank row must not
        // become a DettaglioLinee with nothing to describe.
        lineItems: submittedLineItems(lineItems),
        totalAmount: totals.totaleText,
        // A plain value, unlike `customerId`: the API replaces the whole block
        // with what is sent, so what leaves here has to be the *whole* stored
        // block and not just the five fields on screen. `buildPagamento()`
        // spreads the rest back out of `paymentTerms.stored` and hands an
        // instalment plan over untouched — see lib/payment.js.
        //
        // `anchorDate: null` is the draft's own condition — no date to count
        // the days from yet — and is why nothing here invents a due date.
        pagamento: buildPagamento({
          terms: paymentTerms,
          importoPagamento: totals.pagabileText,
          anchorDate: null,
          method: paymentMethod,
        }),
      }, { customerId, loadedCustomerId }));
      // The selection is now what the stored draft holds, so the next save is
      // an ordinary one again and leaves the addressing alone.
      loadedCustomerId = customerId;
      saved = true;
    } catch (err) {
      error = err;
    } finally {
      saving = false;
    }
  }

  /**
   * Delete and go back.
   *
   * On a self-invoice this is not a destructive shortcut but the documented way
   * to change one: there is no edit, and a draft holds no number and no date,
   * so deleting and creating it again loses nothing.
   */
  async function remove() {
    if (!confirm($t('drafts.deleteConfirm'))) return;
    deleting = true;
    error = null;
    try {
      await companyInvoicesApi.deleteDraft(companyId, draftId);
      push('/drafts');
    } catch (err) {
      error = err;
      deleting = false;
    }
  }

  // The dialog only reaches this once promotion has actually succeeded — it
  // shows the server's own message and any warnings itself before the operator
  // asks to move on, so there is nothing left to do here but navigate.
  function onPromoted(event) {
    promoting = false;
    push(`/invoices/${event.detail.id}`);
  }
</script>

<Layout>
  <div class="max-w-4xl mx-auto space-y-6">
    <header class="page-header">
      <h1 class="section-title mb-0 flex items-center gap-2">
        {$t('drafts.editTitle')}
      </h1>
      <p class="text-muted text-sm mt-2 measure">{$t('drafts.numberNote')}</p>
    </header>

    <ErrorBanner error={error} raw={typeof error === 'string'} />

    {#if loading}
      <p class="text-sm text-muted">{$t('drafts.loading')}</p>
    {:else if notFound}
      <div class="card p-6">
        <p class="text-sm" style="color: var(--color-text-body);">{$t('drafts.notFound')}</p>
        <a href="/drafts" use:link class="btn btn-secondary btn-sm mt-4 inline-flex">
          {$t('drafts.backToList')}
        </a>
      </div>
    {:else if autofattura}
      <!-- A self-invoice, shown as it is stored. There is no editor here on
           purpose: the parties are the reverse of an ordinary invoice's and a
           save would rebuild them the ordinary way, so the API refuses one. The
           two things an operator can do with it are issue it and delete it. -->
      <div class="card p-6 space-y-4">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="badge badge-blue">{$t('drafts.autofatturaBadge')} {draftRow.tipoDocumento}</span>
          <span class="text-sm" style="color: var(--color-text-body);">
            {$t(`drafts.autofatturaTipo.${draftRow.tipoDocumento}`)}
          </span>
        </div>
        <p class="text-muted text-sm measure">{$t('drafts.autofatturaReadOnly')}</p>

        <dl class="grid gap-3 sm:grid-cols-2 text-sm">
          <div>
            <dt class="text-xs uppercase tracking-wider text-muted">{$t('drafts.autofatturaSupplier')}</dt>
            <dd style="color: var(--color-text-body);">
              {draftRow.metadata?.cedente?.denominazione || '-'}
              <span class="text-muted font-mono text-xs">
                {draftRow.metadata?.cedente?.idPaese || ''}{draftRow.metadata?.cedente?.idCodice || ''}
              </span>
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wider text-muted">{$t('drafts.autofatturaRecipient')}</dt>
            <dd style="color: var(--color-text-body);">
              {draftRow.metadata?.cessionario?.denominazione || '-'}
              <span class="text-muted font-mono text-xs">{draftRow.codiceDestinatario || ''}</span>
            </dd>
          </div>
          {#if draftRow.metadata?.datiGenerali?.causale}
            <div class="sm:col-span-2">
              <dt class="text-xs uppercase tracking-wider text-muted">{$t('invoiceCreate.fields.causale')}</dt>
              <dd style="color: var(--color-text-body);">{draftRow.metadata.datiGenerali.causale}</dd>
            </div>
          {/if}
          {#if draftRow.metadata?.datiGenerali?.fatturaPrincipale}
            <div class="sm:col-span-2">
              <dt class="text-xs uppercase tracking-wider text-muted">{$t('drafts.autofatturaPrincipale')}</dt>
              <dd style="color: var(--color-text-body);">
                {draftRow.metadata.datiGenerali.fatturaPrincipale.numero}
                · {draftRow.metadata.datiGenerali.fatturaPrincipale.data}
              </dd>
            </div>
          {/if}
        </dl>
      </div>

      <div class="card p-0 overflow-hidden">
        <table class="table-modern w-full">
          <thead class="table-header">
            <tr>
              <th class="table-cell text-left eyebrow text-muted">{$t('invoiceCreate.fields.description')}</th>
              <th class="table-cell text-right eyebrow text-muted">{$t('invoiceCreate.fields.quantity')}</th>
              <th class="table-cell text-right eyebrow text-muted">{$t('invoiceCreate.fields.price')}</th>
              <th class="table-cell text-right eyebrow text-muted">{$t('invoiceCreate.fields.vatPercent')}</th>
              <th class="table-cell text-right eyebrow text-muted">{$t('invoiceCreate.fields.total')}</th>
            </tr>
          </thead>
          <tbody>
            {#each draftRow.metadata?.lineItems || [] as line (line.numeroLinea)}
              <tr class="table-row">
                <td class="table-cell text-xs">{line.descrizione}</td>
                <td class="table-cell text-xs font-mono text-right">{line.quantita}</td>
                <td class="table-cell text-xs font-mono text-right">{line.prezzoUnitario}</td>
                <td class="table-cell text-xs font-mono text-right">{line.aliquotaIVA}</td>
                <td class="table-cell text-xs font-mono text-right">{line.prezzoTotale}</td>
              </tr>
            {/each}
          </tbody>
        </table>
        <div class="p-4 text-right text-sm font-mono" style="color: var(--color-text-body);">
          {$t('drafts.total')}: {draftRow.totalAmount}
        </div>
      </div>

      <div class="flex items-center justify-between gap-3">
        <button type="button" class="btn btn-secondary" on:click={remove} disabled={deleting}>
          <Trash2 size={16} class="mr-1" />
          {$t('drafts.delete')}
        </button>
        <button type="button" class="btn btn-primary" on:click={() => (promoting = true)}>
          <Send size={16} class="mr-1" />
          {$t('drafts.promoteConfirm')}
        </button>
      </div>
    {:else}
      <div class="card p-6 space-y-4">
        <div>
          <label class="label" for="draft-customer">{$t('invoiceCreate.fields.customerAutocomplete')}</label>
          <select id="draft-customer" class="input" bind:value={customerId}>
            <option value="">—</option>
            {#each customers as c (c.id)}
              <option value={c.id}>{customerOptionLabel(c, counterpartLabels)}</option>
            {/each}
          </select>
          <p class="mt-1 text-xs text-muted">{$t('drafts.customerHint')}</p>
          <!-- The draft saves either way; the address is what issuing it will need. -->
          {#if selectedCustomer && isSedeIncomplete(selectedCustomer.sede)}
            <div class="alert alert-warning mt-2" role="status">
              <AlertTriangle size={16} class="alert-mark mt-0.5" aria-hidden="true" />
              <div class="min-w-0">
                <p class="alert-title">
                  {isSedeEmpty(selectedCustomer.sede)
                    ? $t('customers.sede.draftWarning.titleMissing', { nome: selectedCustomerName })
                    : $t('customers.sede.draftWarning.titleIncomplete', { nome: selectedCustomerName })}
                </p>
                <p class="alert-message">{$t('customers.sede.draftWarning.body')}</p>
                <a href="/customers" use:link class="text-xs hover:underline" style="color: var(--color-primary);">
                  {$t('customers.sede.draftWarning.action')}
                </a>
              </div>
            </div>
          {/if}
        </div>

        <div>
          <label class="label" for="draft-causale">{$t('invoiceCreate.fields.causale')}</label>
          <input id="draft-causale" type="text" class="input" bind:value={causale}
            placeholder={$t('invoiceCreate.fields.causalePlaceholder')} />
        </div>
      </div>

      <CorrelatiEditor
        bind:correlati
        bind:open={correlatiOpen}
        tipoDocumento={draftRow?.tipoDocumento || 'TD01'}
        formatoTrasmissione={draftRow?.formatoTrasmissione || 'FPR12'}
      />

      <div class="card p-6">
        <h2 class="eyebrow eyebrow-lg mb-4" style="color: var(--color-primary);">
          {$t('invoiceCreate.sections.lineItems')}
        </h2>
        <LineItemsEditor bind:lineItems {totals} />
        <InvoiceTotalsPreview {totals} />
      </div>

      <div class="card p-6">
        <h2 class="eyebrow eyebrow-lg mb-4" style="color: var(--color-primary);">
          {$t('invoiceCreate.sections.payment')}
        </h2>
        <PaymentTermsEditor
          bind:terms={paymentTerms}
          bind:method={paymentMethod}
          bind:presetChosen={paymentPresetChosen}
          {companyId}
          anchorDate={null}
          autoApplyDefault={!hadStoredPagamento}
        />
      </div>

      <div class="flex items-center justify-between gap-3">
        <button type="button" class="btn btn-secondary" on:click={save} disabled={saving}>
          <Save size={16} class="mr-1" />
          {saving ? $t('drafts.saving') : $t('common.save')}
        </button>
        <button type="button" class="btn btn-primary" on:click={() => (promoting = true)}>
          <Send size={16} class="mr-1" />
          {$t('drafts.promoteConfirm')}
        </button>
      </div>

      {#if saved}
        <p class="text-sm text-muted" role="status" aria-live="polite">{$t('drafts.saved')}</p>
      {/if}
    {/if}
  </div>
</Layout>

{#if promoting}
  <PromoteDialog
    {companyId}
    {draftId}
    on:promoted={onPromoted}
    on:cancel={() => (promoting = false)}
  />
{/if}
